import {h, using, text, handler, data, list, visible, style} from '../h'
import {
  createStore,
  createEvent,
  createApi,
  combine,
  sample,
  Store,
  forward,
  launch
} from '@effector/effector'
import {
  generateEventHighlight,
  generateStoreHighlight,
  generateEffectHighlight,
  generateWatchHighlight,
  generateSampleHighlight,
  generateOnHighlight,
  generateForwardHighlight
} from '../highlight/generateHighlight'
import {reshape} from '../../reshape'
import {remap} from '../storeField'
import {runtimeError} from './unitList'
import {
  stores,
  events,
  effects,
  watches,
  ons,
  forwards,
  samples
} from '../runtime/units'

import {addTimelineEntry} from '../appgrid'
import {Highlight, HighlightValue} from '../highlight/Highlight'
import {SHOW_CODE, SHOW_VIZION} from '../env'

function VizionSectionHeader(content) {
  h('header', () => {
    data({vizionSectionHeader: true})
    h('h3', () => {
      text(content)
    })
  })
}

function unwrapNode(store, getTarget) {
  return store.map(
    (node, unsub) => {
      unsub()
      return forward({
        from: node,
        to: getTarget(node)
      })
    },
    () => {}
  )
}
function itemSelection(store) {
  const selectItem = createEvent<EventTarget>()
  const [key] = remap(store, ['key'] as const)
  const isSelected = combine(
    key,
    selectedUnitKey,
    (key, selected) => key === selected
  )
  sample({
    source: key,
    clock: selectItem,
    target: selectUnitKey
  })
  return {isSelected, selectItem}
}

const units = combine(stores, events, effects, (stores, events, effects) => [
  ...stores,
  ...events,
  ...effects
])
const {addStore} = createApi(stores, {
  addStore: (list, item) => [...list, item]
})
const {addEvent} = createApi(events, {
  addEvent: (list, item) => [...list, item]
})
const selectedUnitKey = createStore(null)
const selectedUnit = combine(
  selectedUnitKey,
  units,
  (key, units) => units.find(unit => unit.key === key) || null
)
const {selectUnitKey} = createApi(selectedUnitKey, {
  selectUnitKey(selected, unit) {
    if (selected === unit) return null
    return unit
  }
})
export function Vizion() {
  h('section', () => {
    data({section: 'vizion'})
    style({
      prop: {
        display: SHOW_VIZION ? 'grid' : 'none'
      }
    })
    h('section', () => {
      data({vizion: 'controls'})
      h('fieldset', () => {
        data({controlGroup: true})
        visible(selectedUnit.map(unit => !!unit && unit.unit === 'store'))
        h('legend', () => {
          text('edit')
        })
        h('button', () => {
          data({ui: 'button'})
          h('span', () => {
            text('value')
          })
        })
      })
      h('fieldset', () => {
        data({controlGroup: true})
        h('legend', () => {
          text('add')
        })
        h('button', () => {
          data({ui: 'button'})
          h('span', () => {
            text('Store')
          })
          handler({
            click: addStore.prepend(() => ({
              name: '$' + ((Math.random() * 1e5) | 0).toString(36),
              initialValue: 1,
              value: 1,
              nullable: false,
              type: 'number',
              key: Symbol('store key'),
              unit: 'store',
              //@ts-ignore
              node: createStore(1).graphite
            }))
          })
        })
        h('button', () => {
          data({ui: 'button'})
          h('span', () => {
            text('Event')
          })
          handler({
            click: addEvent.prepend(() => ({
              name: 'e' + ((Math.random() * 1e5) | 0).toString(36),
              key: Symbol('event key'),
              unit: 'event',
              //@ts-ignore
              node: createEvent().graphite
            }))
          })
        })
        h('button', () => {
          data({ui: 'button'})
          h('span', () => {
            text('Effect')
          })
        })
      })
    })
    h('section', () => {
      data({vizion: 'items'})
      h('article', () => {
        data({vizionItems: 'stores'})
        VizionSectionHeader('stores')
        list(stores, ({store}) => {
          const [name, node] = remap(store, ['name', 'node'] as const)
          const state = createStore(store.getState().value)
          addTimelineEntry({
            name: name.getState(),
            value: store.getState().value,
            time: performance.now()
          })
          unwrapNode(node, () => state)
          unwrapNode(node, () => {
            const edge = createEvent<any>()
            edge.map(value => {
              launch(
                addTimelineEntry,
                {
                  name: name.getState(),
                  value,
                  time: performance.now()
                },
                //@ts-ignore
                true
              )
            })
            return edge
          })
          const {isSelected, selectItem} = itemSelection(store)
          h('div', () => {
            data({vizionItem: 'store', vizionItemSelected: isSelected})
            handler({click: selectItem})
            h('div', () => {
              data({vizionItem: 'store-name'})
              text(name)
            })
            h('div', () => {
              data({vizionItem: 'store-value'})
              HighlightValue(state)
            })
          })
        })
      })
      h('article', () => {
        data({vizionItems: 'events'})
        VizionSectionHeader('events')
        list(events, ({store, signal}) => {
          const [name, node] = remap(store, ['name', 'node'] as const)
          unwrapNode(node, () => {
            const edge = createEvent<any>()
            edge.map(value => {
              launch(
                addTimelineEntry,
                {
                  name: name.getState(),
                  value,
                  time: performance.now()
                },
                //@ts-ignore
                true
              )
            })
            return edge
          })
          const {isSelected, selectItem} = itemSelection(store, signal)
          return h('div', () => {
            data({vizionItem: 'event', vizionItemSelected: isSelected})
            handler({click: selectItem})
            text(name)
          })
        })
      })
      h('article', () => {
        data({vizionItems: 'effects'})
        VizionSectionHeader('effects')
        list(effects, ({store, signal}) => {
          const [name, node] = remap(store, ['name', 'node'] as const)
          unwrapNode(node, () => {
            const edge = createEvent<any>()
            edge.map(value => {
              launch(
                addTimelineEntry,
                {
                  name: name.getState(),
                  value,
                  time: performance.now()
                },
                //@ts-ignore
                true
              )
            })
            return edge
          })
          const {isSelected, selectItem} = itemSelection(store, signal)
          return h('div', () => {
            data({vizionItem: 'effect', vizionItemSelected: isSelected})
            handler({click: selectItem})
            text(name)
          })
        })
      })
    })
    h('section', () => {
      data({vizion: 'selectedUnit'})
      VizionSectionHeader('preview')
      h('section', () => {
        const storeCase = selectedUnit.updates.filter({
          fn: unit => !!unit && unit.unit === 'store'
        })
        data({vizionSelectedUnit: 'store'})
        visible(selectedUnit.map(unit => !!unit && unit.unit === 'store'))
        h('header', () => {
          h('h4', () => {
            text(storeCase.map(unit => `store ${unit.name}`))
          })
        })
        h('section', () => {
          const store = createStore(null)
          const {node} = reshape(selectedUnit, {
            node: unit =>
              //@ts-ignore
              (unit && unit.unit === 'store' && unit.node) || store.graphite
          })

          const state = createStore(null)

          unwrapNode(node, node => {
            const id = node.seq[1].data.target.id
            const val = node.reg[id].current
            //@ts-ignore
            if (state.getState() !== val) state.setState(val)
            return state
          })
          HighlightValue(state)
        })
      })
      h('section', () => {
        const eventCase = selectedUnit.updates.filter({
          fn: unit => !!unit && unit.unit === 'event'
        })
        data({vizionSelectedUnit: 'event'})
        visible(selectedUnit.map(unit => !!unit && unit.unit === 'event'))
        h('header', () => {
          h('h4', () => {
            text(eventCase.map(unit => `event ${unit.name}`))
          })
        })
      })
      // h('section', () => {
      //   data({vizionSelectedUnit: 'json'})
      //   HighlightValue(selectedUnit)
      //   visible(selectedUnit.map(unit => !!unit))
      // })
    })
    if (SHOW_CODE) Code()
  })
}

function Code() {
  h('section', () => {
    function createHighlight<T>(list: Store<T[]>, highlighter) {
      return list.map(list =>
        list.reduce((acc, data) => [...acc, ...highlighter(data)], [])
      )
    }
    const highlightedStores = createHighlight(stores, generateStoreHighlight)
    const highlightedEvents = createHighlight(events, generateEventHighlight)
    const highlightedEffects = createHighlight(effects, generateEffectHighlight)
    const highlightedSamples = createHighlight(samples, generateSampleHighlight)
    const highlightedOns = createHighlight(ons, generateOnHighlight)
    const highlightedForwards = createHighlight(
      forwards,
      generateForwardHighlight
    )
    const highlightedWatchers = createHighlight(watches, generateWatchHighlight)

    const highlightedSeparator = createStore([
      {
        type: 'space',
        value: `\n`
      }
    ])

    const highlightedCode = combine(
      highlightedStores,
      highlightedEvents,
      highlightedEffects,
      highlightedOns,
      highlightedForwards,
      highlightedSamples,
      highlightedWatchers,
      highlightedSeparator,
      (stores, events, effects, ons, forwards, samples, watchers, sep) => [
        ...stores,
        ...sep,
        ...events,
        ...sep,
        ...effects,
        ...sep,
        ...ons,
        ...sep,
        ...samples,
        ...sep,
        ...forwards,
        ...sep,
        ...watchers
      ]
    )
    data({vizion: 'code'})
    const header = runtimeError.map(error => {
      if (Object(error) === error && error.message)
        return `Error: ${error.message}`
      return 'code'
    })
    VizionSectionHeader(header)
    Highlight(highlightedCode)
    h('pre', () => {
      // text(plainCode)
    })
  })
}
